/*
 * 悬浮小视频组件的行为逻辑。
 * 状态机: pinned(右下角小窗口) -> expanded(点击视频放大) -> pinned(点缩小)
 *         pinned/expanded -> closed(点关闭, 这次浏览这个页面就不再出现)
 */
(function () {
  if (customElements.get('floating-product-video')) return;

  class FloatingProductVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.mediaEl = this.querySelector('[data-fpv-media]');
      this.playPauseBtn = this.querySelector('[data-fpv-play-pause]');
      this.muteBtn = this.querySelector('[data-fpv-mute]');
      this.minimizeBtn = this.querySelector('[data-fpv-minimize]');
      this.closeBtn = this.querySelector('[data-fpv-close]');
      this.iconPlay = this.querySelector('[data-fpv-icon-play]');
      this.iconPause = this.querySelector('[data-fpv-icon-pause]');
      this.iconMuted = this.querySelector('[data-fpv-icon-muted]');
      this.iconUnmuted = this.querySelector('[data-fpv-icon-unmuted]');

      if (!this.video) return;

      // 保险起见用 JS 再显式设一次, 不完全依赖 Liquid 渲染出来的 muted 属性——
      // 浏览器的自动播放策略认的是 video.muted 这个 IDL 属性, 不一定认 HTML 属性。
      this.video.muted = true;
      this.video.playsInline = true;
      this.safePlay(); // 自动播放被浏览器拦截也没关系, 用户点了播放按钮或点视频放大时会再 play() 一次

      if (this.mediaEl) {
        this.mediaEl.addEventListener('click', this.handleMediaClick.bind(this));
      }
      if (this.playPauseBtn) {
        this.playPauseBtn.addEventListener('click', this.handlePlayPauseClick.bind(this));
      }
      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', this.handleMuteClick.bind(this));
      }
      if (this.minimizeBtn) {
        this.minimizeBtn.addEventListener('click', this.handleMinimizeClick.bind(this));
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this.handleCloseClick.bind(this));
      }

      document.addEventListener('keydown', this.handleKeydown.bind(this));

      this.video.addEventListener('play', this.syncPlayPauseIcon.bind(this));
      this.video.addEventListener('pause', this.syncPlayPauseIcon.bind(this));
      this.video.addEventListener('volumechange', this.syncMuteIcon.bind(this));

      // 切到别的浏览器标签页/切后台时暂停, 回来时(如果当时是播放状态)恢复播放,
      // 避免用户看不到画面的时候视频还在后台一直播。
      document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    }

    get state() {
      return this.dataset.state;
    }

    set state(value) {
      this.dataset.state = value;
    }

    handleMediaClick() {
      if (this.state !== 'pinned') return;
      this.expand();
    }

    handlePlayPauseClick(event) {
      event.stopPropagation();
      if (this.video.paused) {
        this.safePlay();
      } else {
        this.video.pause();
      }
    }

    // 包一层 catch: 如果 play() 的 promise 还没 resolve 就被紧接着的 pause()
    // 打断("interrupted by a call to pause()"), 浏览器会抛一个 AbortError——
    // 不处理的话在某些环境下会变成一个没人 catch 的 promise rejection / 页面
    // 报错。这里统一吞掉, 不影响实际播放/暂停状态(状态以 video 的 paused /
    // play 事件为准, 不依赖这个 promise 有没有 resolve)。
    safePlay() {
      var playAttempt = this.video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(function () {});
      }
      return playAttempt;
    }

    handleMuteClick(event) {
      event.stopPropagation();
      this.video.muted = !this.video.muted;
    }

    handleMinimizeClick(event) {
      event.stopPropagation();
      this.minimize();
    }

    handleCloseClick(event) {
      event.stopPropagation();
      this.close();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.state === 'expanded') {
        this.minimize();
      }
    }

    handleVisibilityChange() {
      if (this.state === 'closed') return;
      if (document.hidden) {
        this.wasPlayingBeforeHidden = !this.video.paused;
        this.video.pause();
      } else if (this.wasPlayingBeforeHidden) {
        this.safePlay();
      }
    }

    expand() {
      this.state = 'expanded';
      this.video.muted = false;
      this.safePlay();
      this.setAttribute('aria-expanded', 'true');
    }

    minimize() {
      this.state = 'pinned';
      this.video.muted = true;
      this.removeAttribute('aria-expanded');
    }

    close() {
      this.state = 'closed';
      this.video.pause();
    }

    syncPlayPauseIcon() {
      if (!this.iconPlay || !this.iconPause) return;
      var isPaused = this.video.paused;
      this.iconPlay.hidden = !isPaused;
      this.iconPause.hidden = isPaused;
      if (this.playPauseBtn) {
        this.playPauseBtn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
      }
    }

    syncMuteIcon() {
      if (!this.iconMuted || !this.iconUnmuted) return;
      var isMuted = this.video.muted;
      this.iconMuted.hidden = !isMuted;
      this.iconUnmuted.hidden = isMuted;
      if (this.muteBtn) {
        this.muteBtn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      }
    }
  }

  customElements.define('floating-product-video', FloatingProductVideo);
})();
